import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Search, Building2 } from 'lucide-react';
import { DatabaseService } from '../services/databaseService';

interface EditorialAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

const EditorialAutocomplete: React.FC<EditorialAutocompleteProps> = ({
  value,
  onChange,
  placeholder = "Ej: Planeta",
  className = "",
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [allEditorials, setAllEditorials] = useState<string[]>([]);
  const [justSelected, setJustSelected] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  
  // Referencias para manejo de debounce
  const debounceTimerRef = useRef<number | null>(null);
  const lastSearchQueryRef = useRef<string>('');

  // Sync internal state with prop value
  useEffect(() => {
    setInputValue(value);
    lastSearchQueryRef.current = value;
  }, [value]);

  // Cargar todas las editoriales al montar el componente
  useEffect(() => {
    const loadEditorials = async () => {
      try {
        const editorials = await DatabaseService.getUniqueEditorials();
        setAllEditorials(editorials);
      } catch (error) {
        console.error('Error loading editorials:', error);
      }
    };
    
    loadEditorials();
  }, []);

  // Función para filtrar editoriales
  const filterEditorials = (query: string) => {
    if (disabled || query.trim().length < 1 || justSelected) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }

    const filtered = allEditorials.filter(editorial =>
      editorial.toLowerCase().includes(query.toLowerCase())
    );
    
    setSuggestions(filtered);
    setIsOpen(filtered.length > 0);
  };

  // Función para manejar el debounce de búsqueda
  const debouncedSearch = (query: string) => {
    // Limpiar timer anterior si existe
    if (debounceTimerRef.current) {
      window.clearTimeout(debounceTimerRef.current);
    }

    // Actualizar el último query buscado
    lastSearchQueryRef.current = query;

    // Establecer nuevo timer
    debounceTimerRef.current = window.setTimeout(() => {
      // Verificar que el query no ha cambiado antes de ejecutar la búsqueda
      if (query === lastSearchQueryRef.current) {
        filterEditorials(query);
      }
    }, 300);
  };

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cleanup effect para cancelar timers al desmontar
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        window.clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Handle mobile scroll behavior
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    
    if (!scrollContainer || !isOpen) return;

    const handleWheel = (e: WheelEvent) => {
      const scrollTop = scrollContainer.scrollTop;
      const scrollHeight = scrollContainer.scrollHeight;
      const height = scrollContainer.clientHeight;
      
      // If we're at the boundaries, let the page scroll
      if ((scrollTop <= 0 && e.deltaY < 0) || 
          (scrollTop + height >= scrollHeight && e.deltaY > 0)) {
        return;
      }
      
      // Otherwise, prevent page scroll
      e.preventDefault();
    };

    scrollContainer.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      scrollContainer.removeEventListener('wheel', handleWheel);
    };
  }, [isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange(newValue);
    
    // Reset justSelected flag when user starts typing manually
    if (justSelected) {
      setJustSelected(false);
    }
    
    // Iniciar búsqueda con debounce solo si no acabamos de seleccionar una editorial
    if (!justSelected) {
      debouncedSearch(newValue);
    }
  };

  const handleEditorialSelect = (editorial: string) => {
    // Cancelar cualquier búsqueda en progreso
    if (debounceTimerRef.current) {
      window.clearTimeout(debounceTimerRef.current);
    }

    setInputValue(editorial);
    lastSearchQueryRef.current = editorial;
    setJustSelected(true);
    onChange(editorial);
    setIsOpen(false);
    
    // Reset the justSelected flag after a delay to prevent immediate re-opening
    setTimeout(() => {
      setJustSelected(false);
    }, 1000);
  };

  const handleInputFocus = () => {
    if (disabled || justSelected) return;
    
    // Mostrar todas las editoriales si no hay texto
    if (inputValue.trim().length === 0) {
      setSuggestions(allEditorials);
      setIsOpen(allEditorials.length > 0);
    } else if (inputValue.trim().length >= 1 && suggestions.length > 0) {
      setIsOpen(true);
    }
  };

  const handleInputBlur = () => {
    // Don't immediately close on blur to allow clicking on suggestions
    // The click outside handler will take care of closing
  };

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      // Cancelar búsqueda en progreso
      if (debounceTimerRef.current) {
        window.clearTimeout(debounceTimerRef.current);
      }
    } else if (e.key === 'Enter' && isOpen && suggestions.length > 0) {
      e.preventDefault();
      handleEditorialSelect(suggestions[0]);
    }
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleInputKeyDown}
          disabled={disabled}
          className={`w-full px-3 py-2 pr-10 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-warning-500 focus:border-transparent transition-colors duration-200 text-sm ${
            disabled ? 'opacity-50 cursor-not-allowed' : ''
          }`}
          placeholder={placeholder}
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
          <Search className="h-4 w-4 text-slate-400" />
        </div>
      </div>

      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.15 }}
          className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg max-h-80 overflow-hidden"
        >
          <div 
            ref={scrollContainerRef}
            className="max-h-80 overflow-y-auto autocomplete-dropdown"
            style={{ 
              overscrollBehavior: 'contain',
              WebkitOverflowScrolling: 'touch'
            }}
          >
            {suggestions.length > 0 && (
              <div className="py-1">
                {suggestions.map((editorial, index) => (
                  <motion.button
                    key={index}
                    whileHover={{ backgroundColor: 'rgba(245, 158, 11, 0.1)' }}
                    onClick={() => handleEditorialSelect(editorial)}
                    className="w-full px-3 py-3 text-left hover:bg-warning-50 dark:hover:bg-warning-900/20 transition-colors duration-150 border-b border-slate-100 dark:border-slate-700 last:border-b-0 touch-manipulation"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 flex items-center justify-center bg-warning-100 dark:bg-warning-900/30 rounded border border-slate-200 dark:border-slate-600">
                          <Building2 className="h-4 w-4 text-warning-600 dark:text-warning-400" />
                        </div>
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-slate-900 dark:text-white truncate">
                          {editorial}
                        </h4>
                      </div>
                    </div>
                  </motion.button>
                ))}
              </div>
            )}
            
            {suggestions.length === 0 && inputValue.trim().length >= 1 && !justSelected && (
              <div className="py-4 px-3 text-center">
                <Building2 className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  No se encontraron editoriales con ese nombre
                </p>
                <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                  Puedes agregarla manualmente
                </p>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default EditorialAutocomplete;